/** What the fixed dashboards draw, as plain data - **no read, no query and no arithmetic beyond
 * formatting** (ADR-0195, option A and the shown-correctly checklist).
 *
 * Components render these models and nothing else, so the rules below are tested here without a
 * browser (`dashboardModel.test.ts`, one test per rule, each naming its id).
 *
 * - **CK-9 (no claim the register rules out).** `FORBIDDEN_PHRASES` lists the words ADR-0195 and
 *   the claims register refuse (a forecast, an "on track", an outlook); every string the model
 *   emits is scanned for them in the test, and no figure is typed in here.
 * - **Absence is not zero.** Scope 3 not assessed is `not_assessed`; a prior period with no figures
 *   is `no_prior`; a count a role is not asked is `null` and is drawn "not shown to your role".
 * - **The list is closed at four** (`DASHBOARD_VIEWS`). A fifth is an amendment to ADR-0195.
 */

import type { DocumentStageCounts, Trends } from "../../api/dashboardReads";
import type { components } from "../../api/schema";
import type { Coverage } from "../../api/sources";
import type { Exchange } from "../../api/suppliers";
import { share, tonnes } from "../../lib/format";
import { keepSearch } from "../search";
import type { ScreenSearch } from "../search";
import type { Screen } from "../useScreen";

export const DASHBOARD_VIEWS = [
  { key: "executive", label: "Executive", about: "Where we are and what is in the way" },
  { key: "data-health", label: "Data health", about: "What arrived, what is missing, how sure we are" },
  { key: "operations", label: "Operations", about: "What is moving and what is stuck" },
  { key: "suppliers", label: "Suppliers", about: "Who has answered and what it is worth" },
] as const;

export type DashboardKey = (typeof DASHBOARD_VIEWS)[number]["key"];

/** What each view will count, three to five labels of a few words: the strip of dashes a view shows
 * while it has nothing to read (no reporting period, or a workspace with no bill yet). A view that
 * has nothing says so once, under its tiles, rather than drawing a dozen empty charts that read as
 * results (KD, 2026-11-02). */
export const DASH_LABELS: Readonly<Record<DashboardKey, readonly string[]>> = {
  executive: ["Total emissions", "Filings", "Needs you", "Target"],
  "data-health": ["Figures with evidence", "Bills arrived", "Gaps", "Grade A figures"],
  operations: ["Waiting on you", "To review", "Missing bills", "Open requests"],
  suppliers: ["Suppliers", "Requests open", "Answered", "Scope 3 shared"],
};

/** Whether the workspace has recorded nothing the dashboards could draw: no figure in the window, no
 * document or approval waiting, no missing bill, no filing owed and no open item. The two reads are
 * the server's; **both must have been read** (a missing one is "not yet", never "nothing"), so a
 * failed or pending read never collapses the views into an empty state. */
export function nothingToShowYet(
  inventory: { totals_by_scope: readonly { figures: number }[] } | undefined,
  summary:
    | {
        counts: {
          approvals_waiting_on_me: number | null;
          blocking_findings: number | null;
          documents_to_review: number | null;
          missing_source_windows: number | null;
          my_open_items: number;
        };
        filings: readonly unknown[];
      }
    | undefined,
): boolean {
  if (!inventory || !summary) return false;
  const { counts } = summary;
  return (
    inventory.totals_by_scope.every((card) => card.figures === 0) &&
    summary.filings.length === 0 &&
    (counts.approvals_waiting_on_me ?? 0) === 0 &&
    (counts.blocking_findings ?? 0) === 0 &&
    (counts.documents_to_review ?? 0) === 0 &&
    (counts.missing_source_windows ?? 0) === 0 &&
    counts.my_open_items === 0
  );
}

/** The words ADR-0195 (3b) and the claims register (`docs/product/07-claims-register.md`) refuse
 * on a dashboard. A tile that needs one of them is a tile that makes a claim EmitIQ cannot back. */
export const FORBIDDEN_PHRASES: readonly string[] = [
  "on track",
  "forecast",
  "projected",
  "predicted",
  "outlook",
  "run-rate",
  "will end",
  "time saved",
  "benchmark",
];

type ScopeTotal = components["schemas"]["ScopeTotalOut"];

export interface ScopeRow {
  scope: "1" | "2" | "3";
  assessed: boolean;
  kg: string;
  /** `1,686.5` - tonnes as printed; absent when not assessed. */
  text?: string;
  /** `69.6%` of the assessed total; absent when there is no total to take a share of. */
  share?: string;
  /** Geometry only (`Number`), for a donut arc. */
  value: number;
  figures: number;
}

export interface ScopeSplit {
  rows: ScopeRow[];
  /** The assessed total, summed exactly once for the centre of the ring. */
  totalKg: string | null;
  totalText: string | null;
}

/** The ring's scopes and shares. **The centre figure is the server's `total_kgco2e`**, passed in:
 * this model adds nothing (finding 8, rules 3 and 5). With no scope assessed there is no total to
 * take a share of and no ring, whatever the server's zero says. */
export function scopeSplit(totals: readonly ScopeTotal[], serverTotalKg: string): ScopeSplit {
  const scopes = ["1", "2", "3"] as const;
  const totalKg = totals.some((candidate) => candidate.assessed) ? serverTotalKg : null;
  const rows = scopes.map((scope): ScopeRow => {
    const total = totals.find((candidate) => candidate.scope === scope);
    if (!total?.assessed) {
      return { scope, assessed: false, kg: total?.kgco2e ?? "0", value: 0, figures: total?.figures ?? 0 };
    }
    return {
      scope,
      assessed: true,
      kg: total.kgco2e,
      text: tonnes(total.kgco2e),
      ...(totalKg ? { share: share(total.kgco2e, totalKg) ?? undefined } : {}),
      value: Math.max(0, Number(total.kgco2e)),
      figures: total.figures ?? 0,
    };
  });
  return { rows, totalKg, totalText: totalKg ? tonnes(totalKg) : null };
}

// ----------------------------------------------------------------------------- the prior period

export type Movement = "up" | "down" | "flat" | "no_prior";

export interface ScopeMovement {
  scope: string;
  movement: Movement;
  /** Tonnes of the current and prior windows, as printed. */
  current: string;
  prior?: string;
  /** `+12.4` or `-3.1` tonnes: the server's own delta, only formatted. */
  delta?: string;
  /** Geometry: the two bars share one scale (the larger of the two is full length). */
  currentFraction: number;
  priorFraction: number;
}

/** Each scope against the prior window, from `/inventory/trends` and nothing else.
 *
 * **A prior with no figures is `no_prior`, never a fall to zero**, and an unassessed side is left
 * out: a movement between two methods is not a movement. The delta is the server's.
 */
export function scopeMovements(trends: Trends): ScopeMovement[] {
  const rows = trends.scopes.filter((scope) => scope.current_figures > 0 || scope.prior_figures > 0);
  return rows.map((scope) => {
    const hasPrior = scope.prior_figures > 0 && scope.prior_kgco2e !== null;
    const current = Math.abs(Number(scope.current_kgco2e));
    const prior = hasPrior ? Math.abs(Number(scope.prior_kgco2e)) : 0;
    const biggest = Math.max(current, prior, 0);
    const delta = hasPrior && scope.delta_kgco2e !== null ? Number(scope.delta_kgco2e) : undefined;
    const movement: Movement = !hasPrior
      ? "no_prior"
      : delta === undefined || tonnes(scope.delta_kgco2e ?? "0") === tonnes("0")
        ? "flat"
        : delta > 0
          ? "up"
          : "down";
    return {
      scope: scope.scope,
      movement,
      current: tonnes(scope.current_kgco2e),
      ...(hasPrior ? { prior: tonnes(scope.prior_kgco2e ?? "0") } : {}),
      ...(delta !== undefined && scope.delta_kgco2e !== null
        ? { delta: `${delta > 0 ? "+" : ""}${tonnes(scope.delta_kgco2e)}` }
        : {}),
      currentFraction: biggest > 0 ? current / biggest : 0,
      priorFraction: biggest > 0 ? prior / biggest : 0,
    };
  });
}

// ----------------------------------------------------------------------------------- the census

export const CENSUS_ORDER = [
  "submitted",
  "declared_nil",
  "pending",
  "unknown",
  "missing",
  "not_expected",
] as const;
export type CensusKey = (typeof CENSUS_ORDER)[number];

export const CENSUS_LABEL: Record<CensusKey, string> = {
  submitted: "Submitted",
  declared_nil: "Confirmed none",
  pending: "Period still open",
  unknown: "Cannot tell",
  missing: "Missing",
  not_expected: "Not expected",
};

export interface CensusBar {
  key: CensusKey;
  label: string;
  count: number;
  fraction: number;
}

/** The census in its **six** states, each kept apart (ADR-0166: no two collapse). `fraction` is the
 * share of the windows counted, geometry only.
 *
 * **`not_expected` is counted from the windows, not from the row's summary**: the five counts a source
 * row carries omit it (`sourceModel.countStates` says why), and the tile's own sentence said "six
 * states that stay apart" over five rows (G-Wviewer-06) - a state left out reads as the state not
 * existing. `expected` is the windows the census actually expects (the first five), because "no source
 * is expected in this period" is judged on those and not on windows outside a source's life. */
export function censusBars(coverage: Coverage): { bars: CensusBar[]; windows: number; expected: number } {
  const counts: Record<CensusKey, number> = {
    submitted: 0,
    declared_nil: 0,
    pending: 0,
    unknown: 0,
    missing: 0,
    not_expected: 0,
  };
  for (const source of coverage.sources) {
    counts.submitted += source.submitted;
    counts.declared_nil += source.declared_nil;
    counts.pending += source.pending;
    counts.unknown += source.unknown;
    counts.missing += source.missing;
    counts.not_expected += (source.windows ?? []).filter((window) => window.state === "not_expected").length;
  }
  const windows = CENSUS_ORDER.reduce((sum, key) => sum + counts[key], 0);
  return {
    windows,
    expected: windows - counts.not_expected,
    bars: CENSUS_ORDER.map((key) => ({
      key,
      label: CENSUS_LABEL[key],
      count: counts[key],
      fraction: windows > 0 ? counts[key] / windows : 0,
    })),
  };
}

export interface SiteGap {
  facilityId: string;
  name: string;
  missing: number;
  unknown: number;
}

/** The sites with a window the census cannot account for, most first. Counts of windows only: no
 * total is derived and nothing here is "what a gap costs" (that is a labelled note of the
 * Coverage screen, not a dashboard figure). */
export function siteGaps(coverage: Coverage, limit = 6): SiteGap[] {
  const byFacility = new Map<string, SiteGap>();
  for (const source of coverage.sources) {
    const row = byFacility.get(source.facility_id) ?? {
      facilityId: source.facility_id,
      name: source.facility_name,
      missing: 0,
      unknown: 0,
    };
    row.missing += source.missing;
    row.unknown += source.unknown;
    byFacility.set(source.facility_id, row);
  }
  return [...byFacility.values()]
    .filter((row) => row.missing + row.unknown > 0)
    .sort((a, b) => b.missing + b.unknown - (a.missing + a.unknown) || a.name.localeCompare(b.name))
    .slice(0, limit);
}

// --------------------------------------------------------------------------------- the documents

export interface StageBar {
  stage: string;
  label: string;
  count: number;
  meaning: string;
  fraction: number;
}

export function stageBars(counts: DocumentStageCounts): StageBar[] {
  const biggest = Math.max(0, ...counts.stages.map((stage) => stage.count));
  return counts.stages.map((stage) => ({
    stage: stage.stage,
    label: stage.stage.replace(/_/g, " ").replace(/^./, (letter) => letter.toUpperCase()),
    count: stage.count,
    meaning: stage.meaning,
    fraction: biggest > 0 ? stage.count / biggest : 0,
  }));
}

// ----------------------------------------------------------------------------------- the exchange

export const REQUEST_STATES = ["open", "answered", "accepted", "declined", "withdrawn"] as const;

export interface RequestBar {
  state: string;
  label: string;
  count: number;
  fraction: number;
}

export function requestBars(buyerSide: readonly Exchange[]): RequestBar[] {
  const biggest = Math.max(
    0,
    ...REQUEST_STATES.map((state) => buyerSide.filter((request) => request.state === state).length),
  );
  return REQUEST_STATES.map((state) => {
    const count = buyerSide.filter((request) => request.state === state).length;
    return {
      state,
      label: state.charAt(0).toUpperCase() + state.slice(1),
      count,
      fraction: biggest > 0 ? count / biggest : 0,
    };
  });
}

/** An open request whose due date has passed. `today` is passed in (an ISO date), never read here. */
export function overdueRequests(buyerSide: readonly Exchange[], today: string): Exchange[] {
  return buyerSide.filter(
    (request) => request.state === "open" && request.due_on !== null && request.due_on < today,
  );
}

/** Answers waiting for the buyer to accept: the point at which somebody takes responsibility for a
 * number (`RequestState`). */
export function waitingToBeAccepted(buyerSide: readonly Exchange[]): Exchange[] {
  return buyerSide.filter((request) => request.state === "answered");
}

export const DASHBOARD_NOT_HERE: ReadonlyArray<{ what: string; why: string }> = [
  {
    what: "A year-end illustration",
    why: "It may be drawn only when the boundary, the sites, the factor edition and the restatement state are the same in both years, and the reads that prove that do not exist yet.",
  },
  {
    what: "Intensity and the biggest movers",
    why: "They wait on a decision that has not been made.",
  },
  {
    what: "An uncertainty band",
    why: "It needs a published method, and none is named yet.",
  },
  {
    what: "A tile you design yourself",
    why: "Composition is where mixed boundaries and mixed periods appear.",
  },
];


/** The search a drill-through link carries: the window in force **explicitly** (a custom period
 * lives in the address bar and `keepSearch(screen)` does not read the window off the screen), the
 * Scope 2 method, and the register's filters. */
export function carryOf(screen: Screen): ScreenSearch {
  return {
    ...keepSearch(screen),
    from: screen.window?.period_start,
    to: screen.window?.period_end,
  };
}
